/**
 * RequirementCard Component
 *
 * Compact, collapsible requirement card with edit/delete actions
 */

import React from "react";
import { Card, Typography, Button, Tooltip, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined, HolderOutlined, LockOutlined } from "@ant-design/icons";
import { RequirementState } from "../utils/formTransformers";

interface RequirementCardProps {
  requirement: RequirementState;
  index: number;
  onEdit: (requirement: RequirementState) => void;
  onDelete: (id: string | number) => void;
  onToggleMustMatch: (id: string | number, mustMatch: boolean) => void;
  isTransitioning?: boolean;
  isLeaving?: boolean;
  mustMatchCount: number;
  isAlreadyAdded: boolean;
  dragHandleProps?: any; // Props for drag handle from @dnd-kit
  isDragging?: boolean;
}

const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement,
  onEdit,
  onDelete,
  onToggleMustMatch,
  isTransitioning = false,
  isLeaving = false,
  mustMatchCount,
  isAlreadyAdded,
  dragHandleProps,
  isDragging = false,
}) => {
  const handleDelete = () => {
    onDelete(requirement.id);
  };

  // Check if delete should be hidden (only one must-match remaining or already added)
  const shouldHideDelete =
    (requirement.mustMatch && mustMatchCount === 1) || isAlreadyAdded;
  
  // Check if edit button should be hidden (already added requirements in edit mode)
  const shouldHideEdit = isAlreadyAdded;

  // Prevent drag when interacting with buttons
  const handleInteractiveElementMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Card
      className={`requirement-card ${
        requirement.mustMatch ? "requirement-card--must-match" : ""
      } ${isTransitioning ? "requirement-card--transitioning" : ""} ${
        isLeaving ? "requirement-card--leaving" : ""
      } ${isDragging ? "requirement-card--dragging" : ""}`}
      style={{
        marginBottom: 8,
        opacity: isDragging ? 0.5 : 1,
      }}
      bodyStyle={{ padding: "6px 10px" }}
      {...(dragHandleProps ? {
        ...dragHandleProps,
        style: {
          cursor: 'grab',
          display: 'flex',
          alignItems: 'stretch',
          gap: 0,
          width: '100%',
          ...(dragHandleProps.style || {}),
        },
      } : {})}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: '100%' }}>
        {/* Drag Icon - shown for draggable cards */}
        {dragHandleProps && (
          <div
            className="requirement-drag-handle"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none', // Icon is visual only, card handles drag
              flexShrink: 0,
              color: 'var(--ant-color-text-secondary)',
            }}
            title="Drag to move between sections"
          >
            <HolderOutlined style={{ fontSize: 16 }} />
          </div>
        )}
        {/* Lock Icon - shown for read-only requirements in edit mode */}
        {isAlreadyAdded && !dragHandleProps && (
          <Tooltip title="This requirement is published and cannot be modified">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'var(--ant-color-text-quaternary)',
              }}
            >
              <LockOutlined style={{ fontSize: 14 }} />
            </div>
          </Tooltip>
        )}

        {/* Description - wrapped text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography.Paragraph
            ellipsis={{ rows: 2, expandable: true }}
            style={{
              lineHeight: 1.5,
              wordWrap: "break-word",
              whiteSpace: "normal",
              marginBottom: 0,
            }}
          >
            {requirement.description} 
          </Typography.Paragraph>
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          {/* <Tooltip title={getToggleTooltipText()} placement="top">
            <Switch
              checked={requirement.mustMatch}
              onChange={handleToggleMustMatch}
              size="small"
              disabled={isToggleDisabled}
            />
          </Tooltip> */}

          {!shouldHideEdit && (
            <Tooltip title="Edit requirement">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(requirement);
                }}
                onMouseDown={handleInteractiveElementMouseDown}
                onPointerDown={handleInteractiveElementMouseDown}
                style={{ padding: "2px 6px", height: 24, pointerEvents: 'auto' }}
              />
            </Tooltip>
          )}

          {!shouldHideDelete && (
            <Popconfirm
              title="Delete requirement?"
              description="This action cannot be undone."
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDelete();
              }}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete requirement">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={handleInteractiveElementMouseDown}
                  onPointerDown={handleInteractiveElementMouseDown}
                  style={{ padding: "2px 6px", height: 24, pointerEvents: 'auto' }}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      </div>
    </Card>
  );
};

export default RequirementCard;
